<template>
  <v-container class="pa-0">
    <p class="textHeadline">{{ t('xp-messenger.partner.create') }}</p>

    <v-container class="mainViewContent">
      <!-- Partner Details Card -->
      <v-card class="mb-4">
        <v-card-title>{{ t('xp-messenger.partner.details') }}</v-card-title>
        <v-card-text>

          <div class="d-flex justify-end mt-4 mb-4">
            <v-combobox
              max-width="400px"
              bg-color="white"
              :placeholder="t('community')"
              v-model="selection.communityEntry"
              :items="communityAvailable"
              :filter-keys="['title', 'value']"
              density="comfortable"
              variant="outlined"
              item-value="ecid"
              rounded="2"
              hide-details
              clearable
            ></v-combobox>
            <SecondaryButton
              class="mr-2 mb-4"
              :loading="xpMessengerStore.loading"
              :disabled="xpMessengerStore.loading"
              @click="handleTakeover"
              :text="t('use')"
            />
          </div>


          <partner-fields
            v-model="partnerDetails"
            :errors="xpMessengerStore.formErrors"
            operation-type="create"
            @clear-error="clearError"
          />

          <!-- Error Display -->
          <v-alert
            v-if="xpMessengerStore.error"
            type="error"
            class="mt-4"
            density="compact"
            closable
            @click:close="xpMessengerStore.error = null"
          >
            {{ xpMessengerStore.error }}
          </v-alert>

          <!-- Form Actions -->
          <div class="d-flex justify-end mt-4">
            <TertiaryButton
              v-if="xpMessengerStore.error"
              class="mr-2"
              @click="clearForm"
              :text="t('clear')"
            />
            <PrimaryButton
              :loading="xpMessengerStore.loading"
              :disabled="xpMessengerStore.loading"
              @click="handleSubmit"
              :text="t('create')"
            />
          </div>
        </v-card-text>
      </v-card>

      <!-- Partner Certificate Card -->
      <v-card class="mb-4">
        <v-card-title>{{ t('xp-messenger.partner.certificate') }}</v-card-title>
        <v-card-text>
          <!-- Partner Selection Dropdown -->
          <v-autocomplete
            v-model="selectedPartnerForCertificate"
            :items="xpMessengerStore.partners"
            item-title="displayName"
            item-value="backendId"
            :label="t('xp-messenger.partner.selectPartner')"
            :hint="t('xp-messenger.partner.selectPartnerHint')"
            persistent-hint
            variant="outlined"
            class="mb-4"
            :loading="xpMessengerStore.loading"
            :disabled="xpMessengerStore.loading || xpMessengerStore.partners.length === 0"
            @update:model-value="onPartnerSelected"
            clearable
            :filter="customFilter"
          >
            <template v-slot:item="{ props, item }">
              <v-list-item v-bind="props">
                <template v-slot:title>
                  {{ item.raw.displayName }}
                </template>
                <template v-slot:subtitle>
                  {{ item.raw.backendId }}
                </template>
              </v-list-item>
            </template>
          </v-autocomplete>

          <!-- Load Partners Button (if no partners loaded) -->
          <div v-if="xpMessengerStore.partners.length === 0 && !xpMessengerStore.loading" class="mb-4">
            <PrimaryButton
              variant="outlined"
              @click="loadPartners"
              :loading="xpMessengerStore.loading"
              :text="t('xp-messenger.partner.loadPartners')"
            />
          </div>

          <partner-fields
            v-model="certificateDetails"
            :errors="xpMessengerStore.formErrors"
            operation-type="certificate"
            :show-password="true"
            @clear-error="clearError"
          />

          <!-- Certificate Request Result -->
          <div v-if="xpMessengerStore.certificateRequestResult" class="mt-4">
            <v-text-field
              v-model="xpMessengerStore.certificateRequestResult.certificateRequest"
              :label="t('xp-messenger.partner.certificateRequest')"
              readonly
              outlined
              hide-details
              class="mb-4"
            >
              <template v-slot:append>
                <v-btn
                  icon
                  @click="copyToClipboard"
                  :disabled="!xpMessengerStore.certificateRequestResult?.certificateRequest"
                >
                  <v-icon>mdi-content-copy</v-icon>
                </v-btn>
              </template>
            </v-text-field>
          </div>

          <!-- Certificate Form Actions -->
          <div class="d-flex justify-end mt-4">
            <PrimaryButton
              :loading="xpMessengerStore.loading"
              :disabled="xpMessengerStore.loading"
              @click="handleCertificateRequest"
              :text="t('xp-messenger.partner.requestCertificate')"
            />
          </div>
        </v-card-text>
      </v-card>
    </v-container>

    <!-- Snackbar handled globally via shared component in App.vue -->
  </v-container>
</template>

<script setup>
import { useXPMessengerStore } from '@/stores/xpMessengerStore';
import {ref, inject, onMounted, computed} from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import PartnerFields from '@/components/PartnerFields.vue';
import { useSnackbarStore } from '@es-bill/es-bill-common-components';
import { useCommunitiesStore } from "@/stores/communitiesStore";
import { PrimaryButton, SecondaryButton, TertiaryButton } from '@es-bill/es-bill-common-components';

const router = useRouter();
const xpMessengerStore = useXPMessengerStore();
const $log = inject('log');
const snackbarStore = useSnackbarStore();
const { t } = useI18n();

const partnerDetails = ref({ backendId: '', displayName: '' });
const certificateDetails = ref({ backendId: '', displayName: '', password: '' });
const selectedPartnerForCertificate = ref('');

const communitiesStore = useCommunitiesStore();

function handleTakeover() {
  if(!selection.value.communityEntry)
    return;

  partnerDetails.value.backendId = selection.value.communityEntry.community.ecn;
  partnerDetails.value.displayName = selection.value.communityEntry.community.name;
}

const selection = ref({
  communityEntry: null,
});


const communityAvailable = computed(() => {
  const communities= communitiesStore.allCommunities;

  return communities.map(c => {
    return {
      title: c.name,
      props: { subtitle: c.ecid },
      ecn: c.ecn,
      ecid: c.ecid,
      community: c,
      overwriteEcnDso: (['16a', 'SC', 'P2P', '16c'].includes(c.communityType)) ? c.ecnDso:undefined,
    };
  })
})

const clearError = (field) => {
  xpMessengerStore.formErrors[field] = null;
  if (xpMessengerStore.error) xpMessengerStore.error = null;
};

const clearForm = () => {
  resetForm();
  xpMessengerStore.error = null;
  xpMessengerStore.formErrors = { backendId: null, displayName: null };
};

const loadPartners = async () => {
  try {
    await xpMessengerStore.fetchPartners($log);
  } catch (error) {
    $log.error('Error loading partners:', error);
  }
};

const onPartnerSelected = (backendId) => {
  xpMessengerStore.certificateRequestResult = null;
  if (backendId) {
    const selectedPartner = xpMessengerStore.partners.find(p => p.backendId === backendId);
    if (selectedPartner) {
      certificateDetails.value.backendId = selectedPartner.backendId;
      certificateDetails.value.displayName = selectedPartner.displayName;
    }
  } else {
    certificateDetails.value.backendId = '';
    certificateDetails.value.displayName = '';
  }
};


const handleSubmit = async () => {
  try {
    await xpMessengerStore.submitPartnerForm(partnerDetails.value, $log);
    snackbarStore.showSnackbar(t('xp-messenger.partner.createSuccess'), '', 'Success', 5000, []);
    resetForm();
  } catch (error) {
    $log.error('Error submitting form:', error);
    let errorMessage = t('xp-messenger.partner.createError');
    if (error.message && error.message.includes('409 Conflict')) {
      try {
        const errorData = JSON.parse(error.message.split(' - ')[1]);
        if (errorData.existingPartner) {
          const existing = errorData.existingPartner;
          errorMessage = `${t('xp-messenger.partner.alreadyExists')} (ID: ${existing.id}, Name: ${existing.displayName})`;
        } else {
          errorMessage = t('xp-messenger.partner.alreadyExists');
        }
      } catch {
        errorMessage = t('xp-messenger.partner.alreadyExists');
      }
    } else if (error.message) {
      errorMessage = error.message;
    }
    snackbarStore.showSnackbar(t('xp-messenger.partner.createError'), errorMessage, 'Error', 10000, []);
  }
};

const handleCertificateRequest = async () => {
  try {
    const selectedPartner = xpMessengerStore.partners.find(p => p.backendId === selectedPartnerForCertificate.value);
    if (!selectedPartner) {
      snackbarStore.showSnackbar(t('xp-messenger.partner.certificateRequestError'), t('xp-messenger.partner.certificateRequestPartnerNotFound'), 'Error', 8000, []);
      return;
    }
    await xpMessengerStore.submitCertificateRequest(selectedPartner.id, certificateDetails.value, $log);
    snackbarStore.showSnackbar(t('xp-messenger.partner.certificateRequestSuccess'), '', 'Success', 5000, []);
    certificateDetails.value.password = '';
  } catch (error) {
    $log.error('Error requesting certificate:', error);
    let errorMessage = t('xp-messenger.partner.certificateRequestError');
    if (error.message && error.message.includes('404')) {
      errorMessage = t('xp-messenger.partner.certificateRequestPartnerNotFound');
    } else if (error.message && error.message.includes('400')) {
      try {
        const errorData = JSON.parse(error.message.split(' - ')[1]);
        if (errorData.type === 'validation' && errorData.error === 'Password Validation Error') {
          errorMessage = errorData.message;
        } else {
          errorMessage = t('xp-messenger.partner.certificateRequestInvalidData');
        }
      } catch {
        errorMessage = t('xp-messenger.partner.certificateRequestInvalidData');
      }
    } else if (error.message) {
      errorMessage = error.message;
    }
    snackbarStore.showSnackbar(t('xp-messenger.partner.certificateRequestError'), errorMessage, 'Error', 10000, []);
  }
};

const copyToClipboard = async () => {
  if (xpMessengerStore.certificateRequestResult?.certificateRequest) {
    try {
      await navigator.clipboard.writeText(xpMessengerStore.certificateRequestResult.certificateRequest);
      snackbarStore.showSnackbar(t('xp-messenger.partner.certificateCopied'), '', 'Success', 5000, []);
    } catch (err) {
      $log.error('Failed to copy text:', err);
      snackbarStore.showSnackbar(t('xp-messenger.partner.copyError'), '', 'Error', 5000, []);
    }
  }
};

const resetForm = () => {
  partnerDetails.value = { backendId: '', displayName: '' };
  certificateDetails.value = { backendId: '', displayName: '', password: '' };
  selectedPartnerForCertificate.value = '';
  xpMessengerStore.certificateRequestResult = null;
};

const customFilter = (item, query) => {
  if (!query) return true;
  const queryText = query.toLowerCase();
  const displayName = item.raw.displayName?.toLowerCase() || '';
  const backendId = item.raw.backendId?.toLowerCase() || '';
  return displayName.includes(queryText) || backendId.includes(queryText);
};

onMounted(async () => {
  xpMessengerStore.certificateRequestResult = null;
    await loadPartners();
});
</script>

<style scoped>
.mainViewContent {
  margin-top: 16px;
}
</style>
